/**
 * clipboard.ts — a small, resilient copy-to-clipboard helper for the staff
 * surfaces (the SCR quick-copy block, invite links, re-entry links).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: the async Clipboard API is unavailable in some contexts (older browsers,
 * jsdom, insecure origins). This wraps it with a legacy `execCommand('copy')`
 * fallback and always resolves a boolean, so a copy button can reflect success
 * or failure without ever throwing.
 */

/** Copy `text` to the clipboard. Resolves true on success, false otherwise. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'absolute';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}
